import { useState, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useErrorHandling } from '@/utils/errorHandling';
import { PlandayCompanyResponse, PlandaySetupRequest } from '@/models/payment';
import { PlandayCompanySelect } from './PlandayCompanySelect';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { TwoInputConnection } from '@/pages/company/common/TwoInputConnection';

interface PlandayConnectProps {
    onClose?: () => void;
    onSaveSuccess: any;
}

export const PlandayConnect = (props: PlandayConnectProps) => {
    const { t } = useTranslation();
    const [plandayData, setPlandayData] = useState<PlandaySetupRequest>(
        {} as any
    );
    const { setAlert } = useNotificationsSnackbar();
    const { backendErrorHandler } = useErrorHandling();
    const [companyInfo, setCompanyInfo] = useState<PlandayCompanyResponse>();

    const { setupPlanday } = usePaymentsMutations();

    const dispatch = useAppDispatch();
    const [isReconnect, setIsReconnect] = useState(false);

    useLayoutEffect(() => {
        if (setupPlanday.isSuccess) {
            const data: PlandayCompanyResponse = setupPlanday.data?.data;
            setAlert('Connected successfully', 'success');
            if (data?.children?.length > 1) {
                setCompanyInfo(data);
            } else {
                props.onSaveSuccess?.();
            }
            if (isReconnect) {
                setIsReconnect(false);
            }
        }
    }, [setupPlanday.isSuccess]);

    useEffect(() => {
        dispatch(setLoader(setupPlanday.isPending));

        if (setupPlanday.isError) {
            backendErrorHandler('Could not connect with Planday');
        }
    }, [setupPlanday]);

    const onClick = () => {
        dispatch(setLoader(true));
        setupPlanday.mutate({
            clientId: plandayData?.clientId,
            refreshToken: plandayData?.refreshToken
        });
    };

    const handleReconnect = () => {
        setIsReconnect(true);
        setCompanyInfo(undefined);
    };

    return (
        <>
            {(!companyInfo || isReconnect) && (
                <TwoInputConnection
                    input1Value={plandayData?.clientId}
                    input2Value={plandayData?.refreshToken}
                    input1Label={t('client_id')}
                    input2Label={t('refresh_token')}
                    input1Placeholder={t('client_id_placeholder')}
                    input2Placeholder={t('refresh_token_placeholder')}
                    input1OnChange={(val: any) => {
                        setPlandayData({
                            ...plandayData,
                            clientId: val.target.value
                        });
                    }}
                    input2OnChange={(val) => {
                        setPlandayData({
                            ...plandayData,
                            refreshToken: val.target.value
                        });
                    }}
                    primaryDisabled={
                        !plandayData.clientId || !plandayData?.refreshToken
                    }
                    primaryButtonTitle={t('connect')}
                    secondaryButtonTitle={t('cancel')}
                    onClickPrimary={onClick}
                    onClickSecondary={() => {
                        if (isReconnect) {
                            setIsReconnect(false);
                        } else {
                            props.onClose?.();
                        }
                    }}
                />
            )}

            <PlandayCompanySelect
                onClose={props.onClose}
                onReconnect={handleReconnect}
                companyInfo={companyInfo}
                clientId={plandayData?.clientId ?? undefined}
                refreshToken={plandayData?.refreshToken ?? undefined}
                onSaveSuccess={props.onSaveSuccess}
                show={(companyInfo?.children?.length ?? 0) > 1 && !isReconnect}
            />
        </>
    );
};
